// frontend/src/components/PostgreSQLComponents.jsx
import React, { useState, useEffect } from 'react';
import { useAuth } from '../AuthContext';
import './PostgreSQLComponents.css'; // Будет один CSS файл

// ============================================
// КОМПОНЕНТ: PostgreSQLManager
// ============================================

export const PostgreSQLManager = ({ containers = [], onRefreshContainers, onNavigateToContainer }) => {
  const { token } = useAuth();
  const [databases, setDatabases] = useState([]);
  const [availableContainers, setAvailableContainers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [serverStatus, setServerStatus] = useState(null);
  const [stats, setStats] = useState(null);
  const [filterContainer, setFilterContainer] = useState('all');
  
  const API_BASE_URL = 'http://app.1c-lxc.ru';

  // Загрузка списка БД пользователя
  const fetchUserDatabases = async () => {
    if (!token) return;
    
    setIsLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/postgres/databases`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        }
      });

      if (response.status === 401) {
        return;
      }

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setDatabases(data.databases || []);
          
          // После загрузки баз данных, обновляем контейнеры
          if (onRefreshContainers) {
            onRefreshContainers();
          }
        }
      }
    } catch (error) {
      console.error('Ошибка загрузки БД:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Загрузка доступных контейнеров для создания БД
  const fetchAvailableContainers = async () => {
    if (!token) return;
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/postgres/containers/available`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setAvailableContainers(data.containers || []);
        }
      }
    } catch (error) {
      console.error('Ошибка загрузки доступных контейнеров:', error);
    }
  };

  // Загрузка статуса сервера
  const fetchServerStatus = async () => {
    if (!token) return;
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/postgres/status`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setServerStatus(data);
        if (data.success && data.server) {
          setStats(data.server);
        }
      }
    } catch (error) {
      console.error('Ошибка получения статуса PostgreSQL:', error);
    }
  };

  useEffect(() => {
    if (token) {
      fetchUserDatabases();
      fetchAvailableContainers();
      fetchServerStatus();
    }
  }, [token]);

  // Обработчик создания новой БД
  const handleDatabaseCreated = (newDatabase) => {
    setDatabases(prev => [newDatabase, ...prev]);
    setShowCreateModal(false);
    fetchAvailableContainers();
    
    // Обновляем контейнеры после создания БД
    if (onRefreshContainers) {
      console.log('Вызов onRefreshContainers после создания БД');
      onRefreshContainers();
    }
  };

  // Обработчик удаления БД
  const handleDatabaseDeleted = (dbName) => {
    setDatabases(prev => prev.filter(db => db.db_name !== dbName));
    fetchAvailableContainers();
    
    // Обновляем контейнеры после удаления БД
    if (onRefreshContainers) {
      console.log('Вызов onRefreshContainers после удаления БД');
      onRefreshContainers();
    }
  };

  // Обработчик сброса пароля
  const handlePasswordReset = (dbName) => {
    alert(`Пароль для базы данных ${dbName} успешно изменен и сохранен в системе`);
    // После сброса пароля также обновляем данные
    fetchUserDatabases();
    if (onRefreshContainers) {
      onRefreshContainers();
    }
  };

  // Форматирование размера
  const formatBytes = (bytes) => {
    if (bytes === 0 || !bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Фильтрация баз данных по контейнеру
  const filteredDatabases = filterContainer === 'all' 
    ? databases 
    : databases.filter(db => db.container_vmid === filterContainer);

  // Получение имени контейнера по ID
  const getContainerName = (vmid) => {
    const container = containers.find(c => c.vmid === vmid) || 
                     availableContainers.find(c => c.vmid === vmid);
    return container ? container.name : `Контейнер ${vmid}`;
  };

  return (
    <div className="postgresql-container">
      <div className="postgresql-header">
        <div className="header-left">
          <h2>
            <i className="fas fa-database"></i> 
            PostgreSQL базы данных
          </h2>
          {serverStatus && serverStatus.status === 'healthy' ? (
            <span className="server-status healthy">
              <i className="fas fa-check-circle"></i> Сервер работает
            </span>
          ) : (
            <span className="server-status unhealthy">
              <i className="fas fa-exclamation-triangle"></i> Сервер недоступен
            </span>
          )}
        </div>
        <button 
          className="create-db-btn"
          onClick={() => setShowCreateModal(true)}
          disabled={!serverStatus || serverStatus.status !== 'healthy' || availableContainers.length === 0}
          title={availableContainers.length === 0 ? 'Нет доступных запущенных контейнеров' : ''}
        >
          <i className="fas fa-plus"></i> Создать базу данных
        </button>
      </div>

      {/* Статистика сервера */}
      {stats && (
        <div className="server-stats">
          <div className="stat-card">
            <div className="stat-icon">
              <i className="fas fa-server"></i>
            </div>
            <div className="stat-content">
              <span className="stat-label">Версия PostgreSQL</span>
              <span className="stat-value">{stats.version || 'Unknown'}</span>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">
              <i className="fas fa-hdd"></i>
            </div>
            <div className="stat-content">
              <span className="stat-label">Хост:Порт</span>
              <span className="stat-value">{stats.host}:{stats.port}</span>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">
              <i className="fas fa-plug"></i>
            </div>
            <div className="stat-content">
              <span className="stat-label">Подключения</span>
              <span className="stat-value">
                {stats.active_connections || 0} / {stats.max_connections || '∞'}
              </span>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">
              <i className="fas fa-cubes"></i>
            </div>
            <div className="stat-content">
              <span className="stat-label">Ваши БД</span>
              <span className="stat-value">{databases.length}</span>
            </div>
          </div>
        </div>
      )}

      {/* Фильтр по контейнерам */}
      {databases.length > 0 && (
        <div className="filter-section">
          <label htmlFor="container-filter">Фильтр по контейнеру:</label>
          <select 
            id="container-filter"
            value={filterContainer}
            onChange={(e) => setFilterContainer(e.target.value)}
            className="container-filter"
          >
            <option value="all">Все контейнеры</option>
            {[...new Set(databases.map(db => db.container_vmid))].map(vmid => (
              <option key={vmid} value={vmid}>
                {getContainerName(vmid)} (VMID: {vmid})
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Список баз данных */}
      <div className="postgresql-content">
        {isLoading ? (
          <div className="loading-container">
            <div className="spinner"></div>
            <p>Загрузка баз данных...</p>
          </div>
        ) : filteredDatabases.length > 0 ? (
          <div className="databases-grid">
            {filteredDatabases.map(db => (
              <PostgresDatabaseCard
                key={db.db_name}
                database={db}
                onDelete={handleDatabaseDeleted}
                onPasswordReset={handlePasswordReset}
                onRefresh={fetchUserDatabases}
                formatBytes={formatBytes}
                containerName={getContainerName(db.container_vmid)}
                token={token}
                API_BASE_URL={API_BASE_URL}
                onRefreshContainers={onRefreshContainers}
                onNavigateToContainer={onNavigateToContainer} // Передаем функцию навигации
              />
            ))}
          </div>
        ) : (
          <div className="empty-state">
            <div className="empty-icon">
              <i className="fas fa-database fa-4x"></i>
            </div>
            <h3>У вас пока нет баз данных</h3>
            <p>Создайте первую базу данных PostgreSQL на одном из ваших контейнеров</p>
            {availableContainers.length > 0 ? (
              <button 
                className="create-first-db-btn"
                onClick={() => setShowCreateModal(true)}
              >
                <i className="fas fa-plus"></i> Создать базу данных
              </button>
            ) : (
              <div className="warning-message">
                <i className="fas fa-exclamation-triangle"></i>
                <p>Нет запущенных контейнеров. Запустите контейнер на вкладке "Сервера 1C" для создания БД.</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Информация о безопасности */}
      <div className="security-info">
        <div className="info-card">
          <i className="fas fa-shield-alt"></i>
          <div>
            <h4>Безопасное хранение данных</h4>
            <p>Пароли к базам данных генерируются автоматически и хранятся только на сервере. Вы не видите пароль - это обеспечивает максимальную безопасность.</p>
          </div>
        </div>
        <div className="info-card">
          <i className="fas fa-link"></i>
          <div>
            <h4>Привязка к контейнеру</h4>
            <p>Каждая база данных привязана к конкретному контейнеру 1С. Это обеспечивает изоляцию данных между разными проектами.</p>
          </div>
        </div>
      </div>

      {/* Модальное окно создания БД */}
      {showCreateModal && (
        <PostgresCreateModal
          onClose={() => setShowCreateModal(false)}
          onSuccess={handleDatabaseCreated}
          containers={availableContainers}
          token={token}
          API_BASE_URL={API_BASE_URL}
        />
      )}
    </div>
  );
};

// ============================================
// КОМПОНЕНТ: PostgresCreateModal
// ============================================

const PostgresCreateModal = ({ onClose, onSuccess, containers, token, API_BASE_URL }) => {
  const [formData, setFormData] = useState({
    db_name: '',
    container_vmid: containers.length > 0 ? containers[0].vmid : '',
    description: '',
    encoding: 'UTF8',
    connection_limit: 50
  });

  const [availableEncodings, setAvailableEncodings] = useState(['UTF8', 'WIN1251']);
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState('');
  const [step, setStep] = useState(1); // 1: ввод данных, 2: создание, 3: результат
  const [createdDb, setCreatedDb] = useState(null);

  // Загрузка доступных кодировок
  useEffect(() => {
    const fetchOptions = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/postgres/versions`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          if (data.available_encodings) {
            setAvailableEncodings(data.available_encodings);
          }
        }
      } catch (error) {
        console.error('Ошибка загрузки опций:', error);
      }
    };

    fetchOptions();
  }, [token, API_BASE_URL]);

  // Валидация имени БД
  const validateDbName = (name) => {
    if (!name) return 'Введите имя базы данных';
    if (name.length < 3) return 'Имя должно содержать минимум 3 символа';
    if (name.length > 50) return 'Имя не должно превышать 50 символов';
    if (!/^[a-zA-Z][a-zA-Z0-9_]*$/.test(name)) {
      return 'Имя должно начинаться с буквы и содержать только буквы, цифры и подчеркивания';
    }
    return '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const nameError = validateDbName(formData.db_name);
    if (nameError) {
      setError(nameError);
      return;
    }

    if (!formData.container_vmid) {
      setError('Выберите контейнер');
      return;
    }

    setError('');
    setIsCreating(true);
    setStep(2);

    try {
      const response = await fetch(`${API_BASE_URL}/api/postgres/databases`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setCreatedDb(data.database);
        setStep(3);
        onSuccess(data.database);
      } else {
        setError(data.detail || 'Ошибка создания базы данных');
        setStep(1);
        setIsCreating(false);
      }
    } catch (error) {
      console.error('Ошибка создания БД:', error);
      setError('Ошибка сети: ' + error.message);
      setStep(1);
      setIsCreating(false);
    }
  };

  // Находим выбранный контейнер
  const selectedContainer = containers.find(c => c.vmid === formData.container_vmid);

  return (
    <div className="modal-overlay" onClick={() => !isCreating && onClose()}>
      <div className="modal-content postgres-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>
            <i className="fas fa-database"></i> 
            {step === 1 && 'Создать базу данных на контейнере'}
            {step === 2 && 'Создание базы данных...'}
            {step === 3 && 'База данных создана!'}
          </h3>
          <button 
            className="close-modal-btn" 
            onClick={onClose}
            disabled={isCreating && step !== 3}
          >
            ×
          </button>
        </div>

        {step === 1 && (
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>Контейнер 1С *</label>
              <select
                value={formData.container_vmid}
                onChange={(e) => setFormData({...formData, container_vmid: e.target.value})}
                required
                disabled={isCreating}
                className="container-select"
              >
                <option value="">Выберите контейнер</option>
                {containers.map(container => (
                  <option key={container.vmid} value={container.vmid}>
                    {container.name} (VMID: {container.vmid}) - {container.ip || 'IP не определен'}
                    {container.databases_count > 0 && ` - БД: ${container.databases_count}`}
                  </option>
                ))}
              </select>
              {selectedContainer && (
                <div className="container-info-hint">
                  <i className="fas fa-info-circle"></i>
                  <span>
                    Будет создана на контейнере <strong>{selectedContainer.name}</strong> 
                    {selectedContainer.databases_count > 0 && ` (уже есть ${selectedContainer.databases_count} БД)`}
                  </span>
                </div>
              )}
            </div>

            <div className="form-group">
              <label>Имя базы данных *</label>
              <input
                type="text"
                value={formData.db_name}
                onChange={(e) => setFormData({...formData, db_name: e.target.value})}
                placeholder="my_database"
                required
                disabled={isCreating}
                autoFocus
              />
              <small className="input-hint">
                Только латинские буквы, цифры и подчеркивание. Начинаться с буквы.
              </small>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Кодировка</label>
                <select
                  value={formData.encoding}
                  onChange={(e) => setFormData({...formData, encoding: e.target.value})}
                  disabled={isCreating}
                >
                  {availableEncodings.map(enc => (
                    <option key={enc} value={enc}>{enc}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Лимит подключений</label>
                <input
                  type="number"
                  value={formData.connection_limit}
                  onChange={(e) => setFormData({...formData, connection_limit: parseInt(e.target.value)})}
                  min="1"
                  max="1000"
                  disabled={isCreating}
                />
              </div>
            </div>

            <div className="form-group">
              <label>Описание (необязательно)</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder="Назначение базы данных, проект, окружение..."
                rows="2"
                disabled={isCreating}
              />
            </div>

            {error && (
              <div className="error-message">
                <i className="fas fa-exclamation-circle"></i> {error}
              </div>
            )}

            <div className="info-box important">
              <i className="fas fa-shield-alt"></i>
              <div>
                <strong>Важно! Безопасность данных</strong>
                <ul>
                  <li>✓ Пароль генерируется автоматически и хранится только на сервере</li>
                  <li>✓ Вы НЕ увидите пароль - это обеспечивает безопасность</li>
                  <li>✓ Для каждой БД создается отдельный пользователь PostgreSQL</li>
                  <li>✓ Пользователь имеет доступ ТОЛЬКО к своей базе данных</li>
                  <li>✓ База данных привязывается к выбранному контейнеру 1С</li>
                </ul>
              </div>
            </div>

            <div className="modal-actions">
              <button type="button" className="cancel-btn" onClick={onClose}>
                Отмена
              </button>
              <button type="submit" className="submit-btn" disabled={isCreating}>
                {isCreating ? (
                  <>
                    <i className="fas fa-spinner fa-spin"></i> Создание...
                  </>
                ) : (
                  'Создать базу данных'
                )}
              </button>
            </div>
          </form>
        )}

        {step === 2 && (
          <div className="creation-progress">
            <div className="progress-spinner">
              <i className="fas fa-database fa-4x"></i>
              <i className="fas fa-cog fa-2x cog-spin"></i>
            </div>
            <div className="progress-text">
              <h4>Создание базы данных...</h4>
              <p>Пожалуйста, подождите. Это займет несколько секунд.</p>
            </div>
            <div className="progress-steps">
              <div className="progress-step-item">
                <span className="step-indicator active"></span>
                <span className="step-label">Проверка прав доступа</span>
              </div>
              <div className="progress-step-item">
                <span className="step-indicator active"></span>
                <span className="step-label">Создание пользователя БД</span>
              </div>
              <div className="progress-step-item">
                <span className="step-indicator"></span>
                <span className="step-label">Создание базы данных</span>
              </div>
              <div className="progress-step-item">
                <span className="step-indicator"></span>
                <span className="step-label">Настройка прав доступа</span>
              </div>
            </div>
          </div>
        )}

        {step === 3 && createdDb && (
          <div className="creation-success">
            <div className="success-icon">
              <i className="fas fa-check-circle"></i>
            </div>
            
            <div className="database-info">
              <h4>База данных успешно создана!</h4>
              
              <div className="info-block">
                <div className="info-row">
                  <span className="info-label">Имя БД:</span>
                  <span className="info-value">{createdDb.db_name}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Пользователь:</span>
                  <span className="info-value">{createdDb.username}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Контейнер:</span>
                  <span className="info-value">{createdDb.container_name} (VMID: {createdDb.container_vmid})</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Хост PostgreSQL:</span>
                  <span className="info-value">{createdDb.host}:{createdDb.port}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Размер:</span>
                  <span className="info-value">{createdDb.size_mb.toFixed(2)} MB</span>
                </div>
              </div>
              
              <div className="security-note">
                <i className="fas fa-lock"></i>
                <div>
                  <strong>Пароль сохранен в защищенном хранилище</strong>
                  <p>Пароль для доступа к базе данных хранится на сервере и не доступен пользователю. 
                  Это обеспечивает максимальную безопасность ваших данных.</p>
                  <p className="small">Для подключения к базе данных используются внутренние механизмы системы.</p>
                </div>
              </div>
            </div>
            
            <div className="modal-actions">
              <button className="submit-btn" onClick={onClose}>
                Готово
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ============================================
// КОМПОНЕНТ: PostgresDatabaseCard - ИСПРАВЛЕННЫЙ
// ============================================

const PostgresDatabaseCard = ({ 
  database, 
  onDelete, 
  onPasswordReset, 
  onRefresh, 
  formatBytes,
  containerName,
  token,
  API_BASE_URL,
  onRefreshContainers,
  onNavigateToContainer // Добавлена функция навигации
}) => {
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [isResettingPassword, setIsResettingPassword] = useState(false);

  const {
    db_name,
    original_request,
    description,
    status,
    size_mb,
    connections,
    connection_limit,
    created_at,
    username,
    container_vmid,
    container_name,
    container_ip,
    host,
    port
  } = database;

  const displayContainerName = container_name || containerName || `Контейнер ${container_vmid}`;

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleDelete = async () => {
    if (!token) return;
    
    setIsDeleting(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/postgres/databases/${db_name}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        onDelete(db_name);
        setShowConfirmDelete(false);
        
        // Обновляем контейнеры после удаления БД
        if (onRefreshContainers) {
          console.log('Вызов onRefreshContainers после удаления БД из карточки');
          onRefreshContainers();
        }
      } else {
        const data = await response.json();
        alert(`Ошибка удаления: ${data.detail || 'Неизвестная ошибка'}`);
      }
    } catch (error) {
      console.error('Ошибка удаления БД:', error);
      alert(`Ошибка сети: ${error.message}`);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleResetPassword = async () => {
    if (!token) return;
    
    setIsResettingPassword(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/postgres/databases/${db_name}/reset-password`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (response.ok && data.success) {
        onPasswordReset(db_name);
        onRefresh();
        
        // Обновляем контейнеры после сброса пароля
        if (onRefreshContainers) {
          console.log('Вызов onRefreshContainers после сброса пароля');
          onRefreshContainers();
        }
      } else {
        alert(`Ошибка сброса пароля: ${data.detail || 'Неизвестная ошибка'}`);
      }
    } catch (error) {
      console.error('Ошибка сброса пароля:', error);
      alert(`Ошибка сети: ${error.message}`);
    } finally {
      setIsResettingPassword(false);
    }
  };

  // Функция для навигации к карточке контейнера
  const handleContainerClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (onNavigateToContainer) {
      // Создаем объект контейнера с минимальными данными
      const container = {
        vmid: container_vmid,
        name: displayContainerName,
        ip: container_ip
      };
      onNavigateToContainer(container);
    }
  };

  const getStatusColor = () => {
    switch (status) {
      case 'active': return '#28a745';
      case 'error': return '#dc3545';
      default: return '#6c757d';
    }
  };

  const getStatusText = () => {
    switch (status) {
      case 'active': return 'Активна';
      case 'error': return 'Ошибка';
      default: return 'Неизвестно';
    }
  };

  return (
    <div className={`database-card ${status}`}>
      <div className="card-header">
        <div className="db-icon">
          <i className="fas fa-database"></i>
        </div>
        <div className="db-info">
          <h3 className="db-name" title={db_name}>
            {original_request || db_name}
            {original_request && original_request !== db_name && (
              <span className="db-alias" title={`Фактическое имя: ${db_name}`}>
                <i className="fas fa-tag"></i>
              </span>
            )}
          </h3>
          <div className="db-meta">
            {/* ИСПРАВЛЕНИЕ: теперь это кликабельная ссылка */}
            <span className="db-container" title="Контейнер 1С">
              <i className="fas fa-cube"></i>{' '}
              <a 
                href="#" 
                className="container-link"
                onClick={handleContainerClick}
                style={{ 
                  color: '#0066cc',
                  textDecoration: 'none',
                  cursor: 'pointer'
                }}
                onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
                title={`Перейти к контейнеру ${displayContainerName}`}
              >
                {displayContainerName}
              </a>
            </span>
            <span className="db-status" style={{ color: getStatusColor() }}>
              ● {getStatusText()}
            </span>
          </div>
        </div>
        <button 
          className="expand-btn"
          onClick={() => setShowDetails(!showDetails)}
          title={showDetails ? 'Скрыть детали' : 'Показать детали'}
        >
          <i className={`fas fa-chevron-${showDetails ? 'up' : 'down'}`}></i>
        </button>
      </div>

      <div className="card-stats">
        <div className="stat">
          <i className="fas fa-hdd"></i>
          <span className="stat-value">{size_mb ? `${size_mb.toFixed(2)} MB` : '—'}</span>
          <span className="stat-label">Размер</span>
        </div>
        <div className="stat">
          <i className="fas fa-plug"></i>
          <span className="stat-value">{connections || 0}</span>
          <span className="stat-label">
            {connection_limit > 0 ? `/ ${connection_limit}` : 'подкл.'}
          </span>
        </div>
        <div className="stat">
          <i className="fas fa-calendar"></i>
          <span className="stat-value">{formatDate(created_at).split(',')[0]}</span>
          <span className="stat-label">Создана</span>
        </div>
      </div>

      {showDetails && (
        <div className="card-details">
          <div className="details-section">
            <h4>Информация о подключении</h4>
            <div className="details-grid">
              <div className="detail-row">
                <span className="detail-label">Пользователь БД:</span>
                <span className="detail-value">{username}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Хост PostgreSQL:</span>
                <span className="detail-value">{host}:{port}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Контейнер 1С:</span>
                <span className="detail-value">
                  <a 
                    href="#" 
                    className="container-link"
                    onClick={handleContainerClick}
                    style={{ 
                      color: '#0066cc',
                      textDecoration: 'none'
                    }}
                    onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                    onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
                  >
                    {displayContainerName}
                  </a>
                  {' '}(VMID: {container_vmid})
                  {container_ip && `, IP: ${container_ip}`}
                </span>
              </div>
            </div>
          </div>

          {description && (
            <div className="details-section">
              <h4>Описание</h4>
              <p className="db-description">{description}</p>
            </div>
          )}

          <div className="security-badge">
            <i className="fas fa-lock"></i>
            <span>
              <strong>Безопасное хранение:</strong> Пароль хранится на сервере и не доступен пользователю
            </span>
          </div>

          <div className="card-actions">
            <button 
              className="action-btn reset-password-btn"
              onClick={handleResetPassword}
              disabled={isResettingPassword || status !== 'active'}
              title="Сгенерировать новый пароль (старый станет недействительным)"
            >
              {isResettingPassword ? (
                <i className="fas fa-spinner fa-spin"></i>
              ) : (
                <i className="fas fa-key"></i>
              )}
              Сбросить пароль
            </button>
            <button 
              className="action-btn delete-btn"
              onClick={() => setShowConfirmDelete(true)}
              disabled={isDeleting}
              title="Удалить базу данных"
            >
              <i className="fas fa-trash"></i>
              Удалить
            </button>
          </div>
        </div>
      )}

      {/* Диалог подтверждения удаления */}
      {showConfirmDelete && (
        <div className="confirm-dialog">
          <div className="confirm-dialog-content">
            <div className="confirm-icon">
              <i className="fas fa-exclamation-triangle"></i>
            </div>
            <h4>Удалить базу данных?</h4>
            <p>
              Вы уверены, что хотите удалить базу данных <strong>{original_request || db_name}</strong>?
            </p>
            <p className="warning-text">
              <i className="fas fa-exclamation-circle"></i>
              Все данные будут безвозвратно удалены! Пользователь БД также будет удален.
            </p>
            <div className="confirm-actions">
              <button 
                className="cancel-btn"
                onClick={() => setShowConfirmDelete(false)}
                disabled={isDeleting}
              >
                Отмена
              </button>
              <button 
                className="delete-confirm-btn"
                onClick={handleDelete}
                disabled={isDeleting}
              >
                {isDeleting ? (
                  <i className="fas fa-spinner fa-spin"></i>
                ) : (
                  'Удалить навсегда'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};